import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Stack } from "@mui/system";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { CheckboxElement, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { useGetAllTESRawQuery } from "@/api/hooks/platform-service-hooks";
import { useSysCfgCombinedQuery, useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { AAutocompleteElement } from "@/components/a-autocomplete";
import {
  ACC_OS_TP,
  ACC_TP,
  osType,
} from "@/components/config-modals/router-edit-modal/constants";
import { trimSplitCsvRecord } from "@/lib/common-utils";
import {
  ACCOUNT_RELATION_CONFIGS_NAMES,
  ACCOUNT_RELATION_SERVICE_CONFIGS_NAMES,
  ROOM_CONFIGS_NAMES,
  ROOM_NAMES,
} from "@/lib/constants";
import {
  GenerateAccountsDto,
  SysAccountGenerateDto,
  SysCfgRawDto,
  SysCfgRegularDto,
} from "@/shared/api";
import { NilObj } from "@/types/common-types";

const schema = z.object({
  amount: z.number().int().min(1).max(1000).optional(),
  osType: z.number().int().min(0).max(3),
  gameGroup: z.string(),
  source: z.string().nullable().optional(),
  authGroups: z.array(z.string()),
  tesId: z.string(),
  proxies: z.array(z.any()),
  accounts: z.string().optional(),
});

interface IGenerateAccountsFormData {
  enableGenerateByAmount: boolean;
  amount: number;
  tesId: string;
  osType: number;
  gameGroup: string;
  source?: string;
  authGroups: string[];
  accounts: string;
  proxies: any[];
}

type CsvDelimiter = "," | ";";

const SysAccountEditModal = ({
  isOpen = false,
  onSave,
  onClose,
  confirmLoading = false,
  roomName,
}: {
  mode: "add" | "edit";
  accountType: "service" | "regular";
  isOpen?: boolean;
  roomName: string;
  onSave: (accountGenerateByNumberDto: GenerateAccountsDto) => void;
  onClose: () => void;
  confirmLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const csvDelimiter = ",";

  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery({
    enabled: isOpen,
  });

  const {
    data: proxiesRs,
    isLoading: proxiesIsLoading,
    refetch,
  } = useSysCfgQuery({
    domain: roomName,
    config_name: ROOM_CONFIGS_NAMES.PROXY,
    filters: new Set([{ key: "enabled", mode: "EQUALS", value: true as unknown as object }]),
    sort: [{ key: "created_at", mode: "DESC" }],
  });

  const proxies = useMemo(() => proxiesRs?.data.content ?? [], [proxiesRs]);

  const { data: tesListRs, isLoading: tesListIsLoading } = useGetAllTESRawQuery(
    { roomName },
    { enabled: isOpen }
  );

  const { data: selectConfigsRs } = useSysCfgCombinedQuery(
    {
      domain: roomName,
      config_names: ACCOUNT_RELATION_CONFIGS_NAMES,
    },
    {
      enabled: isOpen,
    }
  );

  const { data: selectServiceConfigsRs } = useSysCfgCombinedQuery(
    {
      domain: ROOM_NAMES.SERVICES,
      config_names: ACCOUNT_RELATION_SERVICE_CONFIGS_NAMES,
    },
    {
      enabled: isOpen,
    }
  );

  const configs = useMemo(
    () =>
      new Map(
        (selectConfigsRs?.data ?? [])
          .concat(selectServiceConfigsRs?.data ?? [])
          .map((config) => [
            config.config_name,
            config.configs.sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { numeric: true })
            ),
          ])
      ),
    [selectConfigsRs, selectServiceConfigsRs]
  );

  const tesList = useMemo(() => {
    return tesListRs?.data.filter((tes) => tes.mode === 0) ?? [];
  }, [tesListRs]);

  const authGroups = useMemo(() => {
    return authGroupsRs?.data ?? [];
  }, [authGroupsRs]);

  const {
    control,
    reset,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IGenerateAccountsFormData>({
    mode: "onChange",
    defaultValues: { ...{ amount: 0 } },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    reset({ ...{ amount: 0 } });
  }, [isOpen, reset]);

  function convertCsvInputToAccountsCreateObj(input: string) {
    const records = input.split("\n");

    return records.map((item) => convertCsvRecordToAccountJson(item));
  }

  function convertCsvRecordToAccountJson(record: string): SysAccountGenerateDto {
    const values = trimSplitCsvRecord(record, csvDelimiter);

    return {
      name: values[0],
      authGroups: new Set(getValues("authGroups")),
      password: values[1],
      walletCode: values[2],
      gameGroup: values[3],
      proxy: values[4],
      osType: values[5] as unknown as number,
      accountType: ACC_TP.MANAGER,
    };
  }

  const submit = handleSubmit((data) => {
    const accounts = data.accounts
      ? convertCsvInputToAccountsCreateObj(data.accounts)
      : undefined;
    onSave({
      authGroups: new Set(data.authGroups),
      amount: data.amount,
      tesId: data.tesId,
      osType: data.osType,
      gameGroup: data.gameGroup,
      source: data.source,
      proxies: data.proxies?.map((el) => el.name) ?? [],
      accounts: accounts,
    });
  });

  const handleSave = () => {
    void submit();
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>{t("generateAccounts")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ paddingTop: "5px", width: "400px" }}>
          <AAutocompleteElement
            name="authGroups"
            loading={authGroupsIsLoading}
            control={control}
            label={t("authGroups")}
            required
            options={authGroups.map((it) => it.name)}
            multiple
            textFieldProps={{
              error: !!errors.authGroups,
              helperText: errors.authGroups?.message ?? " ",
              variant: "standard",
            }}
          />

          <AAutocompleteElement
            name="tesId"
            loading={tesListIsLoading}
            control={control}
            label={t("Tes ID")}
            required
            options={tesList.map((it) => it.id)}
            textFieldProps={{
              error: !!errors.tesId,
              helperText: errors.tesId?.message ?? " ",
              variant: "standard",
            }}
          />

          <AAutocompleteElement
            name="osType"
            control={control}
            label={t("osType")}
            options={roomName === "aa_eco" ? [ACC_OS_TP.IOS] : osType.map((i) => i.value)} //Kostyl asked FF i IS. For AA available only IOS
            autocompleteProps={{
              //readOnly: disabled,
              getOptionLabel: (option) => osType.map((i) => i.label)[option] ?? t("unknown"),
              disableClearable: true,
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.osType,
              helperText: errors.osType?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="gameGroup"
            control={control}
            label={t("gameGroup")}
            required
            options={[...(configs.get("game_group") ?? []).map((i) => i.name)]}
            autocompleteProps={
              {
                //readOnly: disabled,
              }
            }
            textFieldProps={{
              //placeholder: "Leave empty for random value",
              variant: "standard",
              error: !!errors.gameGroup,
              helperText: errors.gameGroup?.message ?? " ",
            }}
          />

          <AAutocompleteElement
            name="proxies"
            control={control}
            label={t("proxies")}
            multiple
            options={proxies /*configs.get("proxies")*/ ?? []}
            autocompleteProps={{
              //filterSelectedOptions: true, // чтобы уже выбранные не показывались в списке
              getOptionLabel: (option) => option.name ?? option,

              filterOptions: (options, params) => {
                //search by name, country, city
                const input = params.inputValue.toLowerCase();

                return options.filter((option) => {
                  const fields = [
                    option.name ?? "",
                    (option.data as any)?.country ?? "",
                    (option.data as any)?.city ?? "",
                  ];

                  return fields.some((f) => f.toLowerCase().includes(input));
                });
              },
            }}
            textFieldProps={{
              variant: "standard",
              error: !!errors.proxies,
              helperText: errors.proxies?.message ?? " ",
            }}
          />

          <TextFieldElement
            name="source"
            control={control}
            label="Source"
            error={!!errors.amount}
            helperText={errors.amount?.message ?? " "}
            variant="standard"
            placeholder="source"
          />

          <div style={{ display: "flex", alignItems: "start" }}>
            <CheckboxElement
              control={control}
              name="enableGenerateByAmount"
              label={t("generateByAmount")}
            />

            <TextFieldElement
              name="amount"
              control={control}
              label=""
              type="number"
              disabled={!watch("enableGenerateByAmount")}
              inputProps={{ pattern: "\\d*", style: { marginTop: "5px" } }}
              error={!!errors.amount}
              helperText={errors.amount?.message ?? " "}
              variant="standard"
              placeholder="amount"
            />
          </div>

          <TextFieldElement
            name="accounts"
            control={control}
            label={t("accounts")}
            disabled={watch("enableGenerateByAmount")}
            placeholder={t("separateByCommaFormat")}
            multiline
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button sx={{ width: "100px" }} variant="outlined" onClick={onClose}>
          {t("cancel")}
        </Button>

        <Button
          sx={{ width: "100px" }}
          variant="contained"
          disabled={confirmLoading}
          startIcon={confirmLoading ? <CircularProgress size={20} /> : undefined}
          onClick={handleSave}
        >
          {t("generate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SysAccountEditModal;
